import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FilterService, GridComponent } from '@syncfusion/ej2-angular-grids';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { VendorService } from 'src/app/services/Vendor.service';
import { SYNCFUSION_MODULES, ANGULAR_MODULES } from 'src/app/Shared/shared-imports';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import printJS from 'print-js';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Swal from 'sweetalert2';
import { GeneralService } from 'src/app/services/general.service';
import { EBaseTemplateID } from 'src/app/Shared/enum';
import { PhoneFormatPipe } from 'src/app/Shared/phone-format.pipe';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';
import { PrinterSettingsService } from 'src/app/services/printer-settings.service';

@Component({
  selector: 'app-ops-vendor-history',
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, WordReplacePipe, PhoneFormatPipe, LoaderComponent],
  standalone: true,
  providers: [FilterService],
  templateUrl: './ops-vendor-history.html',
  styleUrl: './ops-vendor-history.scss'
})
export class OpsVendorHistory implements OnInit, OnDestroy {
  @Input() vendorId: number;
  @ViewChild('gridHistory', { static: false }) public gridHistory: GridComponent;

  private destroy$ = new Subject<void>();
  private filterChange$ = new Subject<void>();

  vendorInfo: any;
  pdfUrl: SafeResourceUrl | null = null;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  onlyWithPayment: boolean = false;
  listLatest: boolean = false;
  latestCount: number | null;
  public gridData: any[] = [];
  loaderIcon: Promise<boolean>;
  uniqueSessionId = "";
  laserThermalReceipt: boolean = false;
  maxExpiryDate: Date = new Date();

  constructor(private vendorService: VendorService, private sanitizer: DomSanitizer, private generalService: GeneralService, private wordReplacePipe: WordReplacePipe,
    private printerService: PrinterSettingsService
  ) { }

  ngOnInit(): void {
    const today = new Date();
    this.fromDate = this.getSixMonthsAgoFirstDay();
    this.toDate = today;
    const tabId = sessionStorage.getItem('tabId');
    const sessionKey = `UniqueSessionId-${tabId}`;
    this.uniqueSessionId = localStorage.getItem(sessionKey);
    this.getVendorInfo();
    this.loadVendorHistory();
    this.loadPrinterSettings();
    this.filterChange$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.loadVendorHistory());
  }

  private getSixMonthsAgoFirstDay(): Date {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  onFromDateChange(): void {

    if (!this.fromDate || !this.toDate) {
      return;
    }

    if (this.fromDate > this.toDate) {

      Swal.fire({
        icon: 'warning',
        title: 'Invalid Date Range',
        text: 'From Date cannot be later than To Date.'
      });

      this.fromDate = new Date(this.toDate);
      return;
    }

    this.filterChange$.next();
  }

  onToDateChange(): void {

    if (!this.fromDate || !this.toDate) {
      return;
    }

    if (this.toDate < this.fromDate) {

      Swal.fire({
        icon: 'warning',
        title: 'Invalid Date Range',
        text: 'To Date cannot be earlier than From Date.'
      });

      this.toDate = new Date(this.fromDate);
      return;
    }

    this.filterChange$.next();
  }

  onFilterChange() {
    this.filterChange$.next();
  }

  getVendorInfo() {
    this.vendorService.GetVendorById(this.vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res) this.vendorInfo = res;
      });
  }

  private formatDateLocal(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  loadVendorHistory() {
    const payload = {
      VendorId: this.vendorId,
      FromDate: this.formatDateLocal(this.fromDate),
      ToDate: this.formatDateLocal(this.toDate),
      OnlyWithPayment: this.onlyWithPayment,
      LatestCount: this.listLatest ? this.latestCount : null
    };

    this.loaderIcon = Promise.resolve(true);
    this.vendorService.GetVendorHistory(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.gridData = res.map(el => {
          let RentalType = '';
          switch (el.RentalType) {
            case 1: RentalType = 'Daily'; break;
            case 4: RentalType = 'Monthly'; break;
            case 5: RentalType = 'Weekly'; break;
          }

          let BookingType = 'Booking';
          if (el.Transferred) {
            BookingType = 'Transfer';
          } else if (el.Refund && !el.Deleted) {
            BookingType = 'Deleted';
          } else if (el.Payment) {
            if (el.Refund && el.Deleted) BookingType = 'Void Payment';
            else BookingType = 'Payment';
          } else if (el.Refund && el.Deleted) {
            BookingType = 'Void';
          } else if (el.Deposit) {
            BookingType = 'Payment';
          }

          this.loaderIcon = Promise.resolve(false);
          return {
            ...el,
            RentalType,
            BookingType
          };
        });
        this.loaderIcon = Promise.resolve(false);
      });
  }

  onListLatestToggle() {
    if (this.listLatest) {
      this.fromDate = null;
      this.toDate = null;
    } else {
      this.latestCount = null;
      const today = new Date();
      this.fromDate = this.getSixMonthsAgoFirstDay();
      this.toDate = today;
    }

    this.filterChange$.next();
  }

  allowOnlyDigitsForLatest(event: Event, maxDigits?: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (maxDigits && value.length > maxDigits) {
      value = value.substring(0, maxDigits);
    }
    input.value = value;
    this.latestCount = value ? Number(value) : null;

    if (this.listLatest && this.latestCount) {
      this.filterChange$.next();
    }
  }

  private getReceiptApi(row: any, payload: any) {
    if (row.BookingType === 'Deleted') {
      return this.vendorService.DisplayDeleteRentalReceipt(payload);
    }

    if (row.BookingType === 'Void' || row.BookingType === 'Void Payment') {
      return this.vendorService.DisplayVoidReceipt(payload);
    }

    return this.vendorService.GeneratePaymentReceipt(payload);
  }

  async checkPrinterSettingsOnPrint(row: any) {
    const res = await lastValueFrom(
      this.printerService.getRentalPrinterSettings()
    );

    // Default values
    let thermalPrinting = false;
    let thermalNoOfCopies = 1;
    let rentalTypeNum = 1;
    let customProtocolUrl = "";

    const rentalTypeMap: any = {
      Daily: { num: 1, thermal: res.DailyThermal, copies: res.DailyCopies },
      Monthly: { num: 4, thermal: res.MonthlyThermal, copies: res.MonthlyCopies },
      Weekly: { num: 5, thermal: res.WeeklyThermal, copies: res.WeeklyCopies }
    };

    if (rentalTypeMap[row.RentalType]) {
      const rt = rentalTypeMap[row.RentalType];
      rentalTypeNum = rt.num;
      thermalPrinting = rt.thermal;
      thermalNoOfCopies = rt.copies;
      customProtocolUrl = 'silentprinter://?methodname=RentalPaymentReceipt&param1=' + row.AccountReceiptId + '&param2=' + rentalTypeNum + '&param3=' + this.vendorId + '&param4=False&param5=' + thermalNoOfCopies + '&uniqueSessionId=' + this.uniqueSessionId;
    }

    const bookingTypeMap: any = {
      Deleted: {
        thermal: res.DeleteRentalThermal,
        copies: res.DeleteRentalCopies,
        customProtocolUrl: 'silentprinter://?methodname=deletetransactionreceipt&param1=' + row.AccountReceiptId + '&param2=' + rentalTypeNum + '&param3=' + this.vendorId + '&param4=false&param5=' + thermalNoOfCopies + '&uniqueSessionId=' + this.uniqueSessionId
      },
      Void: {
        thermal: res.VoidRentalThermal,
        copies: res.VoidRentalCopies,
        customProtocolUrl: 'silentprinter://?methodname=voidtransactionreceipt&param1=' + row.AccountReceiptId + '&param2=' + rentalTypeNum + '&param3=' + this.vendorId + '&param4=false&param5=' + thermalNoOfCopies + '&uniqueSessionId=' + this.uniqueSessionId
      },
      'Void Payment': {
        thermal: res.VoidRentalThermal,
        copies: res.VoidRentalCopies,
        customProtocolUrl: 'silentprinter://?methodname=voidtransactionreceipt&param1=' + row.AccountReceiptId + '&param2=' + rentalTypeNum + '&param3=' + this.vendorId + '&param4=true&param5=' + thermalNoOfCopies + '&uniqueSessionId=' + this.uniqueSessionId
      },
      Payment: {
        thermal: res.PaymentThermal,
        copies: res.PaymentCopies,
        customProtocolUrl: 'silentprinter://?methodname=RentalPaymentReceipt&param1=' + row.AccountReceiptId + '&param2=' + rentalTypeNum + '&param3=' + this.vendorId + '&param4=true&param5=' + thermalNoOfCopies + '&uniqueSessionId=' + this.uniqueSessionId
      }
    };

    if (bookingTypeMap[row.BookingType]) {
      thermalPrinting = bookingTypeMap[row.BookingType].thermal;
      thermalNoOfCopies = bookingTypeMap[row.BookingType].copies;
      customProtocolUrl = bookingTypeMap[row.BookingType].customProtocolUrl;
    }

    if (!thermalPrinting) {
      this.onPrint(row);
    } else {
      window.location.href = customProtocolUrl;
    }
  }

  async onPrint(row: any) {
    const payload = this.getPayloadForReceipt(row);
    const apiCall = this.getReceiptApi(row, payload);

    this.loaderIcon = Promise.resolve(true);

    apiCall.subscribe({
      next: (response: any) => {
        const pdfDataUrl = response?.Message;
        if (!pdfDataUrl || !pdfDataUrl.startsWith('data:application/pdf;base64,')) {
          this.loaderIcon = Promise.resolve(false);
          Swal.fire('Error', 'Invalid PDF data in response', 'error');
          return;
        }

        const pdfBase64 = pdfDataUrl.split(',')[1];
        this.loaderIcon = Promise.resolve(false);
        printJS({ printable: pdfBase64, type: 'pdf', base64: true });
      },
      error: () => {
        this.loaderIcon = Promise.resolve(false);
        Swal.fire('Error', 'Failed to generate receipt for print', 'error');
      }
    });
  }

  onView(row: any) {
    const payload = this.getPayloadForReceipt(row);
    const apiCall = this.getReceiptApi(row, payload);

    this.loaderIcon = Promise.resolve(true);
    this.pdfUrl = null;

    apiCall.subscribe({
      next: (response: any) => {
        const pdfDataUrl = response?.Message;

        if (!pdfDataUrl || !pdfDataUrl.startsWith('data:application/pdf;base64,')) {
          Swal.fire('Error', 'Invalid PDF data in response', 'error');
          this.loaderIcon = Promise.resolve(false);
          return;
        }

        const pdfBase64 = pdfDataUrl.split(',')[1];
        const blob = this.base64ToBlob(pdfBase64, 'application/pdf');
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));

        this.loaderIcon = Promise.resolve(false);

        const modalEl = document.getElementById('viewReceiptModal');
        if (modalEl) {
          const modalInstance = (window as any)['bootstrap'].Modal.getOrCreateInstance(modalEl);
          modalInstance.show();
        }
      },

      error: () => {
        Swal.fire('Error', 'Failed to load receipt for view', 'error');
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  onEmail(row: any) {
    const accountReceiptId = row.AccountReceiptId;
    const baseTemplateId = EBaseTemplateID.RentalReceipt;
    const fieldName = this.wordReplacePipe.transform('Vendor');
    const subject = `Rental Receipt for ${fieldName} ${this.vendorInfo?.VendorName || ''}`;

    Swal.fire({
      title: 'Send Email',
      text: `Are you sure you want to email this rental receipt to the ${fieldName}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, send it'
    }).then((result) => {
      if (!result.isConfirmed) return;

      const payload = this.getPayloadForReceipt(row);
      const apiCall = this.getReceiptApi(row, payload);

      this.loaderIcon = Promise.resolve(true);

      apiCall.subscribe({
        next: (response: any) => {
          const pdfDataUrl = response?.Message;

          if (!pdfDataUrl || !pdfDataUrl.startsWith('data:application/pdf;base64,')) {
            Swal.fire('Error', 'Invalid PDF data in response', 'error');
            this.loaderIcon = Promise.resolve(false);
            return;
          }

          const fileName = `RentalReceipt.pdf`;

          const emailData = [{
            AccountReceiptID: accountReceiptId,
            VendorID: parseInt(this.vendorId.toString(), 10),
            BaseTemplateID: baseTemplateId,
            AttachmentFile: pdfDataUrl,
            AttachmentFileName: fileName,
            FileType: 'application/pdf',
            Subject: subject
          }];

          this.generalService.composeEmails(emailData).subscribe({
            next: (res: any) => {
              if (res?.Success) {
                Swal.fire('Success', 'Rental receipt emailed successfully!', 'success');
              } else {
                Swal.fire('Error', res?.Message || 'Email sending failed', 'error');
              }
              this.loaderIcon = Promise.resolve(false);
            },
            error: () => {
              Swal.fire('Error', 'Failed to send email due to server issue', 'error');
              this.loaderIcon = Promise.resolve(false);
            }
          });
        },
        error: () => {
          Swal.fire('Error', 'Failed to generate rental receipt for email', 'error');
          this.loaderIcon = Promise.resolve(false);
        }
      });
    });
  }

  private base64ToBlob(base64: string, type: string): Blob {
    const byteCharacters = atob(base64);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([byteArray], { type });
  }

  private getPayloadForReceipt(row: any) {
    let rentalTypeNum = 1;
    let receiptId = 85;

    switch (row.RentalType) {
      case 'Daily': rentalTypeNum = 1; receiptId = 85; break;
      case 'Monthly': rentalTypeNum = 4; receiptId = 89; break;
      case 'Weekly': rentalTypeNum = 5; receiptId = 87; break;
    }

    if (row.BookingType === 'Deleted') {
      receiptId = 139;
    }

    if (row.BookingType === 'Void' || row.BookingType === 'Void Payment') {
      receiptId = 113;
    }

    if (row.BookingType === "Payment") {
      receiptId = 112;
    }

    return {
      VendorId: this.vendorId,
      AccountReceiptId: row.AccountReceiptId || 0,
      RentalType: rentalTypeNum,
      PaymentOnly: row.BookingType === "Payment" || row.BookingType === "Void Payment",
      DocumentId: receiptId,
      VendorHistory: true,
      Deposit: false,
      DisplayBillNum: false,
      MerchantCopy: false,
      CustomerCopy: false,
      SignBase64: '',
      PaymentWithBooking: row.BookingType === "Booking" && row.Paid != 0,
      LaserThermalReceipt: this.laserThermalReceipt
    };
  }

  private loadPrinterSettings(): void {
    this.printerService.getPOSPrinterSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.laserThermalReceipt = res.ThermalFormatReceipt ?? false;
      });
  }

  // --- Hide unwanted filters in grid ---
  public created(args1) {
    this.gridHistory.element.querySelectorAll('.e-filterdiv')[this.gridHistory.getColumnIndexByField('RentalCharge')].classList.add('e-hide');
    this.gridHistory.element.querySelectorAll('.e-filterdiv')[this.gridHistory.getColumnIndexByField('OtherCharge')].classList.add('e-hide');
    this.gridHistory.element.querySelectorAll('.e-filterdiv')[this.gridHistory.getColumnIndexByField('TotalDue')].classList.add('e-hide');
    this.gridHistory.element.querySelectorAll('.e-filterdiv')[this.gridHistory.getColumnIndexByField('Paid')].classList.add('e-hide');
    this.gridHistory.element.querySelectorAll('.e-filterdiv')[this.gridHistory.getColumnIndexByField('print')].classList.add('e-hide');
    this.gridHistory.element.querySelectorAll('.e-filterdiv')[this.gridHistory.getColumnIndexByField('email')].classList.add('e-hide');
    this.gridHistory.element.querySelectorAll('.e-filterdiv')[this.gridHistory.getColumnIndexByField('view')].classList.add('e-hide');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
